import { RPA_AUXILIARY_WINDOWS } from './auxiliary-windows';
import { DICTATION_WIDGET_WINDOW_LABEL } from './window-labels';

/**
 * Every Rust-created auxiliary webview and the one route it is built to show.
 * Their capabilities grant only what that route needs, so any other page
 * rendered there fails its IPC calls with "not allowed by ACL".
 */
const AUXILIARY_WINDOW_ROUTES: ReadonlyMap<string, string> = new Map<string, string>([
  ...Object.values(RPA_AUXILIARY_WINDOWS).map(
    (spec) => [spec.label, spec.route] as [string, string]
  ),
  [DICTATION_WIDGET_WINDOW_LABEL, '/dictation-widget'],
  ['appointment-bar', '/appointment-bar'],
  ['copilot-overlay', '/copilot-overlay'],
]);

/** Windows whose capability grants no IPC at all, not even event listening. */
const IPC_LESS_WINDOW_LABELS: ReadonlySet<string> = new Set([
  RPA_AUXILIARY_WINDOWS.cuaBorder.label,
  RPA_AUXILIARY_WINDOWS.rpaFlowElementHighlight.label,
]);

type TauriInternalsMetadata = {
  __TAURI_INTERNALS__?: { metadata?: { currentWindow?: { label?: string } } };
};

/**
 * The current webview's label, read synchronously from the metadata Tauri
 * injects before any page script runs. `undefined` on the web and during SSR.
 */
export function getCurrentWindowLabel(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as TauriInternalsMetadata).__TAURI_INTERNALS__?.metadata
    ?.currentWindow?.label;
}

export function isAuxiliaryWindow(label: string | undefined): boolean {
  return label !== undefined && AUXILIARY_WINDOW_ROUTES.has(label);
}

/** True when an auxiliary window is showing a page other than its own route. */
export function isAuxiliaryWindowOffRoute(
  label: string | undefined,
  pathname: string
): boolean {
  if (label === undefined) return false;
  const route = AUXILIARY_WINDOW_ROUTES.get(label);
  return route !== undefined && route !== pathname;
}

export function isIpcLessWindow(label: string | undefined): boolean {
  return label !== undefined && IPC_LESS_WINDOW_LABELS.has(label);
}
